/**
 * Vitgem — order attribution capture (consent-aware)
 *
 * Stores the visitor's first touch (landing path, referrer, UTM, gclid) and the
 * GA4 client_id/session_id, then writes them into cart attributes so they reach
 * the order and the checkout web pixel:
 *
 * Keys are underscore-prefixed by convention only — cart attributes are never shown
 * to the shopper unless a template prints them, and these land in the order's
 * "Additional details" in admin, which is exactly where they are wanted.
 *
 *   _vg_first   landing|source/medium|campaign   (first touch, never overwritten)
 *   _vg_gclid   Google Ads click id, when present
 *   _vg_ga_cid  GA4 client_id  — the custom pixel sends `purchase` with this id,
 *   _vg_ga_sid  GA4 session_id — so the conversion joins the original session
 *
 * GDPR: gated behind the same consent signals as vitgem-analytics.js. Without
 * consent nothing touches localStorage — the visit lives in sessionStorage only
 * and no GA ids exist anyway (gtag runs cookieless under denied consent).
 */
(function () {
  'use strict';

  var GA4_ID = 'G-34V0NBLMS9';

  function consentAllowed() {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (cp && typeof cp.analyticsProcessingAllowed === 'function') {
        if (cp.analyticsProcessingAllowed()) return true;
      }
    } catch (e) {}
    try {
      if (localStorage.getItem('vg_cookie_consent') === 'accepted') return true;
    } catch (e) {}
    return false;
  }

  function store() {
    return consentAllowed() ? window.localStorage : window.sessionStorage;
  }

  // ── first touch ───────────────────────────────────────────────────────────
  function captureTouch() {
    var params = new URLSearchParams(window.location.search);
    var touch = {
      landing: window.location.pathname,
      referrer: document.referrer || '',
      utm_source: params.get('utm_source') || '',
      utm_medium: params.get('utm_medium') || '',
      utm_campaign: params.get('utm_campaign') || '',
      utm_term: params.get('utm_term') || '',
      utm_content: params.get('utm_content') || '',
      gclid: params.get('gclid') || '',
      ts: new Date().toISOString()
    };
    try {
      var s = store();
      if (!s.getItem('vg_attr_first')) s.setItem('vg_attr_first', JSON.stringify(touch));
      // last touch only matters when it carries campaign data
      if (touch.gclid || touch.utm_source) s.setItem('vg_attr_last', JSON.stringify(touch));
    } catch (e) {}
    return touch;
  }

  // Read from both stores: consent can flip mid-visit, which moves where we write.
  function readTouch(key) {
    for (var i = 0; i < 2; i++) {
      try {
        var raw = (i ? window.sessionStorage : window.localStorage).getItem(key);
        if (raw) return JSON.parse(raw);
      } catch (e) {}
    }
    return null;
  }

  // ── cart attribute sync ───────────────────────────────────────────────────
  function buildAttributes(cb) {
    var first = readTouch('vg_attr_first') || captureTouch();
    var last = readTouch('vg_attr_last');
    var attrs = {
      _vg_first: [first.landing, (first.utm_source || sourceFromReferrer(first.referrer)) + '/' + (first.utm_medium || ''), first.utm_campaign].join('|')
    };
    var gclid = (last && last.gclid) || first.gclid;
    if (gclid) attrs._vg_gclid = gclid;

    // GA ids exist only once gtag has booted with granted consent; the 'get'
    // callbacks simply never fire otherwise, so cap the wait and move on.
    if (typeof window.gtag === 'function' && consentAllowed()) {
      var pending = 2;
      var done = false;
      var finish = function () { if (!done) { done = true; cb(attrs); } };
      window.gtag('get', GA4_ID, 'client_id', function (id) {
        if (id) attrs._vg_ga_cid = String(id);
        if (--pending === 0) finish();
      });
      window.gtag('get', GA4_ID, 'session_id', function (id) {
        if (id) attrs._vg_ga_sid = String(id);
        if (--pending === 0) finish();
      });
      setTimeout(finish, 2000);
    } else {
      cb(attrs);
    }
  }

  function sourceFromReferrer(ref) {
    if (!ref) return 'direct';
    try { return new URL(ref).hostname.replace(/^www\./, ''); } catch (e) { return ''; }
  }

  var syncing = false;
  function syncCartAttributes() {
    if (syncing) return;
    syncing = true;
    buildAttributes(function (attrs) {
      var stamp = JSON.stringify(attrs);
      try {
        if (store().getItem('vg_attr_synced') === stamp) { syncing = false; return; }
      } catch (e) {}
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: attrs })
      }).then(function (r) {
        if (r.ok) { try { store().setItem('vg_attr_synced', stamp); } catch (e) {} }
      }).catch(function () {}).finally(function () { syncing = false; });
    });
  }

  captureTouch();

  // A cart exists once Shopify sets the `cart` cookie — sync on those pages so
  // attributes survive even when the visitor added to cart before this script
  // shipped or on a page we missed.
  if (/(^|;\s*)cart=/.test(document.cookie)) {
    setTimeout(syncCartAttributes, 1500);
  }

  // First add-to-cart creates the cart; give the add request a moment to land.
  document.addEventListener('submit', function (e) {
    if (e.target && e.target.matches && e.target.matches('form[data-type="add-to-cart-form"], form[action*="/cart/add"]')) {
      setTimeout(syncCartAttributes, 1500);
    }
  }, true);

  // Consent granted mid-visit (Pandectes fires this) → GA ids become available.
  document.addEventListener('visitorConsentCollected', function () {
    try { store().removeItem('vg_attr_synced'); } catch (e) {}
    if (/(^|;\s*)cart=/.test(document.cookie)) setTimeout(syncCartAttributes, 1500);
  });
})();
